// ============= Imports =============
import { SignalRContextProps } from '@/contexts/SignalRContext';

// ============= Constants =============
const HUB_NAME = 'workspaceRelayHub';

// ============= Types =============
export interface RemoveClientFromGroupWorkspaceSessionProps {
  invokeHub: SignalRContextProps['invokeHub'];
  workspaceId: string;
  userId: string;
}

// ============= Main Function =============
/**
 * Removes the client from a workspace group in WorkspaceRelayHub.
 * Call this when the user navigates away from a workspace so the backend
 * can broadcast USER_OFFLINE(workspaceId, userId) immediately instead of
 * waiting for the connection to drop.
 *
 * Backend must implement RemoveClientFromGroupAsync(workspaceId) and
 * broadcast USER_OFFLINE to other clients in the group.
 *
 * @param invokeHub Function to invoke SignalR hub methods
 * @param workspaceId The workspace ID to leave
 * @param userId The user ID to leave
 * @returns Promise with API response or error
 */
export const removeClientFromGroupWorkspaceSession = async ({
  invokeHub,
  workspaceId,
  userId,
}: RemoveClientFromGroupWorkspaceSessionProps): Promise<void> => {
  if (!workspaceId || !userId) {
    throw new Error('workspaceId and userId are required');
  }

  try {
    await invokeHub<void>(HUB_NAME, 'RemoveClientFromGroupAsync', workspaceId, userId);
    console.log(`✅ Client left workspace group [${workspaceId}] for user [${userId}]`);
  } catch (error) {
    // Backend may not implement this yet; log but don't break the app
    console.warn(
      `⚠️ Could not leave workspace group [${workspaceId}] for user [${userId}] (backend may not implement RemoveClientFromGroupAsync):`,
      error
    );
  }
};
